import axios from "axios";
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import Select from "react-select";
import ReactQuill from "react-quill";
import {
  ArrowPathIcon,
  CheckCircleIcon,
  ChevronRightIcon,
  DocumentTextIcon,
  EnvelopeIcon,
  PaperAirplaneIcon,
  PencilIcon,
} from "@heroicons/react/24/outline";

import AssessmentContext from "../../../context/assessment/AssessmentContext";
import SnackbarContext from "../../../context/assessment/SnackbarContext";
import AssessmentSessionParticipant from "../../../controller/assessment/assessment_participant/session";
import errorHandler from "../../../helper/assessment/errorHandler";
import { InTrayPasteGuard } from "../../shared/sessionIntegrity/SessionIntegrity";
import PdfViewer from "./PdfViewer";

type ComposerContext = {
  mode: TPIntrayDraftMode;
  email?: TPQuestionaryIntrayEmail;
};

type ComposerValues = {
  sendTo: string[];
  copyTo: string[];
  subject: string;
  content: string;
};

const emptyValues: ComposerValues = {
  sendTo: [],
  copyTo: [],
  subject: "",
  content: "<div><br/><br/><br/></div>",
};

function createUuid() {
  const browserCrypto =
    typeof crypto !== "undefined"
      ? (crypto as Crypto & { randomUUID?: () => string })
      : undefined;
  if (browserCrypto?.randomUUID) {
    return browserCrypto.randomUUID();
  }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (character) => {
    const random = (Math.random() * 16) | 0;
    const value = character === "x" ? random : (random & 0x3) | 0x8;
    return value.toString(16);
  });
}

function quotedContent(email: TPQuestionaryIntrayEmail) {
  const copyTo = email.copyto
    .map((item) => `${item.name}&lt;${item.email}&gt;`)
    .join(", ");
  return `<div><br/><br/><br/><hr/><p>${email.from_name}</p><p>Tanggal: ${
    email.date
  }</p>${copyTo ? `<p>Cc: ${copyTo}</p>` : ""}<br/>${email.content}</div>`;
}

function initialValues(context: ComposerContext): ComposerValues {
  if (!context.email || context.mode === "compose") return emptyValues;
  if (context.mode === "forward") {
    return {
      ...emptyValues,
      subject: `Fwd: ${context.email.subject}`,
      content: quotedContent(context.email),
    };
  }
  return {
    sendTo: [`${context.email.from_name}<${context.email.from_email}>`],
    copyTo: context.email.copyto.map(
      (item) => `${item.name}<${item.email}>`
    ),
    subject: `Re: ${context.email.subject}`,
    content: quotedContent(context.email),
  };
}

function IntrayViewer(props: {
  participantSession: TPParticipantSession | undefined;
  activeSession: TPActiveSession;
}) {
  const { participant } = useContext(AssessmentContext);
  const { setNotif } = useContext(SnackbarContext);
  const [intrayEmailList, setIntrayEmailList] = useState<
    TPQuestionaryIntrayEmail[]
  >([]);
  const [sentEmailList, setSentEmailList] = useState<
    (TPQuestionaryReplyEmail | TPQuestionaryNewEmail)[]
  >([]);
  const [selectedEmail, setSelectedEmail] =
    useState<TPQuestionaryIntrayEmail>();
  const [selectedSentEmail, setSelectedSentEmail] = useState<
    TPQuestionaryReplyEmail | TPQuestionaryNewEmail
  >();
  const [emailAddress, setEmailAddress] = useState<TReactSelect[]>([]);
  const [openInbox, setOpenInbox] = useState(true);
  const [openSent, setOpenSent] = useState(false);
  const [openFile, setOpenFile] = useState(false);
  const [composer, setComposer] = useState<ComposerContext>();
  const [values, setValues] = useState<ComposerValues>(emptyValues);
  const [draftStatus, setDraftStatus] = useState("");
  const [hydrated, setHydrated] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [conflict, setConflict] = useState(false);

  const valuesRef = useRef(values);
  const composerRef = useRef<ComposerContext>();
  const draftRef = useRef<{ clientDraftId: string; version: number }>({
    clientDraftId: createUuid(),
    version: 0,
  });
  const saveChainRef = useRef(Promise.resolve());
  const autosaveTimerRef = useRef<number>();
  const generationRef = useRef(0);
  const conflictRef = useRef(false);
  const saveErrorRef = useRef(false);
  const pendingSavesRef = useRef(0);
  const sendingRef = useRef(false);
  const sendKeyRef = useRef(createUuid());
  const tabIdRef = useRef(createUuid());

  useEffect(() => {
    valuesRef.current = values;
  }, [values]);

  const getReplyEmail = useCallback(async () => {
    if (!props.participantSession) return;
    try {
      const controller = new AssessmentSessionParticipant();
      const response = await controller.getAllReplyIntrayEmail(
        participant.assessment_id.toString(),
        participant.id.toString(),
        props.participantSession.id.toString()
      );
      setSentEmailList(response.data.list);
    } catch (error) {
      setNotif({ type: "error", message: errorHandler(error) });
    }
  }, [
    participant.assessment_id,
    participant.id,
    props.participantSession,
    setNotif,
  ]);

  const getDoc = useCallback(async () => {
    try {
      const controller = new AssessmentSessionParticipant();
      const [emailResponse, addressResponse] = await Promise.all([
        controller.getIntrayEmail(
          participant.assessment_id.toString(),
          participant.id.toString()
        ),
        controller.GetAllEmailAddressList(
          participant.assessment_id.toString(),
          participant.id.toString()
        ),
      ]);
      setIntrayEmailList(emailResponse.data.row);
      setEmailAddress(
        addressResponse.data.list.map(
          (email: { email: string; name: string }) => ({
            value: `${email.name}<${email.email}>`,
            label: `${email.name}<${email.email}>`,
          })
        )
      );
      await getReplyEmail();
    } catch (error) {
      setNotif({ type: "error", message: errorHandler(error) });
    }
  }, [
    getReplyEmail,
    participant.assessment_id,
    participant.id,
    setNotif,
  ]);

  useEffect(() => {
    getDoc();
  }, [getDoc]);

  const draftEmailId = (context = composerRef.current) =>
    context?.email?.intray_simulation_email_id?.toString() ?? "new";

  // Deteksi tab ganda saat composer terbuka (sebelum sempat 409 dari autosave).
  useEffect(() => {
    if (!composer || !props.participantSession) return;
    const sessionId = props.participantSession.id;
    const contextKey = `${sessionId}:${draftEmailId(composer)}:${composer.mode}`;
    const storageKey = `kamara-intray-open:${contextKey}`;
    const tabId = tabIdRef.current;
    let channel: BroadcastChannel | null = null;

    const markConflict = () => {
      if (conflictRef.current) return;
      conflictRef.current = true;
      setConflict(true);
      setDraftStatus("Draf terbuka di tab lain.");
      setNotif({
        type: "error",
        message:
          "Draf ini sudah terbuka di tab lain. Muat ulang draf sebelum melanjutkan.",
      });
    };

    const onPeerMessage = (data: {
      type?: string;
      contextKey?: string;
      tabId?: string;
    }) => {
      if (!data || data.tabId === tabId || data.contextKey !== contextKey) return;
      if (data.type === "ping" && channel) {
        channel.postMessage({ type: "pong", contextKey, tabId });
      }
      if (data.type === "claim" || data.type === "ping" || data.type === "pong") {
        markConflict();
      }
    };

    if (typeof BroadcastChannel !== "undefined") {
      channel = new BroadcastChannel(`kamara-intray-${sessionId}`);
      channel.onmessage = (event) => onPeerMessage(event.data);
      channel.postMessage({ type: "claim", contextKey, tabId });
      channel.postMessage({ type: "ping", contextKey, tabId });
    }

    try {
      const existing = window.localStorage.getItem(storageKey);
      if (existing && existing !== tabId) {
        markConflict();
      }
      window.localStorage.setItem(storageKey, tabId);
    } catch {
      // ignore quota / private mode
    }

    const onStorage = (event: StorageEvent) => {
      if (event.key !== storageKey || !event.newValue || event.newValue === tabId) {
        return;
      }
      markConflict();
    };
    window.addEventListener("storage", onStorage);

    return () => {
      channel?.close();
      window.removeEventListener("storage", onStorage);
      try {
        if (window.localStorage.getItem(storageKey) === tabId) {
          window.localStorage.removeItem(storageKey);
        }
      } catch {
        // ignore
      }
    };
  }, [composer, props.participantSession, setNotif]);

  const openComposer = async (context: ComposerContext) => {
    if (!props.participantSession) return;
    const generation = ++generationRef.current;
    window.clearTimeout(autosaveTimerRef.current);
    await saveChainRef.current.catch(() => undefined);
    pendingSavesRef.current = 0;
    setSaving(false);
    saveErrorRef.current = false;
    composerRef.current = context;
    setComposer(context);
    setSelectedEmail(context.email);
    setSelectedSentEmail(undefined);
    setOpenFile(false);
    setHydrated(false);
    setDirty(false);
    setConflict(false);
    conflictRef.current = false;
    sendKeyRef.current = createUuid();
    const defaults = initialValues(context);
    setValues(defaults);
    setDraftStatus("Memuat draf...");
    try {
      const controller = new AssessmentSessionParticipant();
      const response = await controller.getIntrayDraft(
        props.participantSession.id.toString(),
        draftEmailId(context),
        context.mode,
        participant.access_key
      );
      if (generation !== generationRef.current) return;
      const draft: TPIntrayDraft | null = response.data.row;
      if (draft) {
        draftRef.current = {
          clientDraftId: draft.client_draft_id,
          version: draft.version,
        };
        if (draft.mode === context.mode) {
          setValues({
            sendTo: draft.sendto ? draft.sendto.split(",") : [],
            copyTo: draft.copyto ? draft.copyto.split(",") : [],
            subject: draft.subject,
            content: draft.content,
          });
          setDraftStatus("Draf dipulihkan.");
        } else {
          setDraftStatus("Siap menyimpan draf.");
        }
      } else {
        draftRef.current = { clientDraftId: createUuid(), version: 0 };
        setDraftStatus("Draf belum disimpan.");
      }
    } catch (error) {
      if (generation !== generationRef.current) return;
      setDraftStatus("Draf belum dapat dimuat.");
      setNotif({ type: "error", message: errorHandler(error) });
    } finally {
      if (generation === generationRef.current) setHydrated(true);
    }
  };

  const queueDraftSave = useCallback(
    (force = false) => {
      const session = props.participantSession;
      const context = composerRef.current;
      if (!session || !context || conflictRef.current) {
        return saveChainRef.current;
      }
      const generation = generationRef.current;
      pendingSavesRef.current += 1;
      setSaving(true);
      setDraftStatus("Menyimpan...");
      saveChainRef.current = saveChainRef.current
        .catch(() => undefined)
        .then(async () => {
          if (
            generation !== generationRef.current ||
            (sendingRef.current && !force)
          ) {
            return;
          }
          const current = valuesRef.current;
          const metadata = draftRef.current;
          const controller = new AssessmentSessionParticipant();
          const response = await controller.saveIntrayDraft(
            session.id.toString(),
            draftEmailId(context),
            {
              assessment_participant_session_id: session.id,
              intray_simulation_email_id:
                context.email?.intray_simulation_email_id ?? null,
              client_draft_id: metadata.clientDraftId,
              version: metadata.version,
              mode: context.mode,
              sendto: current.sendTo.join(","),
              copyto: current.copyTo.join(","),
              subject: current.subject,
              content: current.content,
            },
            participant.access_key
          );
          if (generation === generationRef.current) {
            draftRef.current = {
              clientDraftId: response.data.row.client_draft_id,
              version: response.data.row.version,
            };
            saveErrorRef.current = false;
            setDirty(false);
            setDraftStatus("Tersimpan.");
          }
        })
        .catch((error) => {
          if (generation !== generationRef.current) return;
          saveErrorRef.current = true;
          if (axios.isAxiosError(error) && error.response?.status === 409) {
            conflictRef.current = true;
            setConflict(true);
            setDraftStatus("Draf berubah di tab lain.");
            setNotif({
              type: "error",
              message:
                "Draf berubah di tab lain. Muat ulang draf sebelum melanjutkan.",
            });
            return;
          }
          setDraftStatus("Belum tersimpan. Coba lagi.");
          setNotif({ type: "error", message: errorHandler(error) });
        })
        .finally(() => {
          pendingSavesRef.current = Math.max(0, pendingSavesRef.current - 1);
          if (generation === generationRef.current) {
            setSaving(pendingSavesRef.current > 0);
          }
        });
      return saveChainRef.current;
    },
    [participant.access_key, props.participantSession, setNotif]
  );

  useEffect(() => {
    if (!composer || !hydrated || !dirty || conflict || sending) return;
    window.clearTimeout(autosaveTimerRef.current);
    autosaveTimerRef.current = window.setTimeout(
      () => queueDraftSave(),
      900
    );
    return () => window.clearTimeout(autosaveTimerRef.current);
  }, [
    composer,
    conflict,
    dirty,
    hydrated,
    queueDraftSave,
    sending,
    values,
  ]);

  const updateValues = (next: Partial<ComposerValues>) => {
    setValues((current) => ({ ...current, ...next }));
    setDirty(true);
  };

  const closeComposer = () => {
    composerRef.current = undefined;
    setComposer(undefined);
    setDraftStatus("");
    setHydrated(false);
    setDirty(false);
    setConflict(false);
    pendingSavesRef.current = 0;
    setSaving(false);
  };

  const discardDraft = async () => {
    if (!props.participantSession || !composerRef.current) return;
    const context = composerRef.current;
    ++generationRef.current;
    window.clearTimeout(autosaveTimerRef.current);
    await saveChainRef.current.catch(() => undefined);
    try {
      const controller = new AssessmentSessionParticipant();
      await controller.deleteIntrayDraft(
        props.participantSession.id.toString(),
        draftEmailId(context),
        context.mode,
        participant.access_key
      );
      closeComposer();
      setSelectedEmail(context.email);
      setNotif({ type: "success", message: "Draf dihapus." });
    } catch (error) {
      setNotif({ type: "error", message: errorHandler(error) });
    }
  };

  const reloadDraft = () => {
    const context = composerRef.current;
    if (context) openComposer(context);
  };

  const sendEmail = async () => {
    const context = composerRef.current;
    const session = props.participantSession;
    if (
      !context ||
      !session ||
      conflictRef.current ||
      valuesRef.current.sendTo.length === 0 ||
      valuesRef.current.subject.trim() === ""
    ) {
      return;
    }
    window.clearTimeout(autosaveTimerRef.current);
    sendingRef.current = true;
    setSending(true);
    setDraftStatus("Menyiapkan pengiriman...");
    try {
      await queueDraftSave(true);
      await saveChainRef.current;
      if (conflictRef.current || saveErrorRef.current) return;
      const current = valuesRef.current;
      const metadata = draftRef.current;
      const payload: TPQuestionaryReplyEmail = {
        assessment_participant_session_id: session.id,
        intray_simulation_email_id:
          context.email?.intray_simulation_email_id ?? null,
        sendto: current.sendTo.join(","),
        copyto: current.copyTo.join(","),
        subject: current.subject,
        content: current.content,
        idempotency_key: sendKeyRef.current,
        client_draft_id: metadata.clientDraftId,
        draft_version: metadata.version,
        mode: context.mode,
      };
      const controller = new AssessmentSessionParticipant();
      await controller.saveReplyIntrayEmail(
        participant.assessment_id.toString(),
        participant.id.toString(),
        payload,
        participant.access_key
      );
      ++generationRef.current;
      closeComposer();
      setSelectedEmail(undefined);
      await getReplyEmail();
      setNotif({ type: "success", message: "Email terkirim." });
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 409) {
        conflictRef.current = true;
        setConflict(true);
        setDraftStatus("Draf berubah atau sesi sudah ditutup.");
        setNotif({
          type: "error",
          message:
            "Email belum dikirim. Muat ulang draf atau tunggu arahan assessor.",
        });
      } else {
        setDraftStatus("Email belum terkirim. Coba lagi.");
        setNotif({ type: "error", message: errorHandler(error) });
      }
    } finally {
      sendingRef.current = false;
      setSending(false);
    }
  };

  const replyCount = (email: TPQuestionaryIntrayEmail) =>
    sentEmailList.filter(
      (item) =>
        item.intray_simulation_email_id ===
        email.intray_simulation_email_id
    ).length;

  return (
    <div className="flex h-[94%] overflow-hidden bg-white">
      <aside className="w-1/4 min-w-[14rem] overflow-auto border-r border-gray-200 lg:w-1/5">
        <button
          type="button"
          onClick={() => {
            closeComposer();
            setSelectedEmail(undefined);
            setSelectedSentEmail(undefined);
            setOpenFile(true);
          }}
          className="flex w-full items-center gap-2 border-b bg-gray-50 px-4 py-3 text-left font-semibold text-gray-700 hover:bg-gray-100"
        >
          <DocumentTextIcon className="w-5" />
          Panduan simulasi
        </button>
        <button
          type="button"
          onClick={() => openComposer({ mode: "compose" })}
          className="flex w-full items-center gap-2 border-b bg-cyan-50 px-4 py-3 text-left font-semibold text-cyan-800 hover:bg-cyan-100"
        >
          <PencilIcon className="w-5" />
          Tulis email
        </button>
        <button
          type="button"
          onClick={() => setOpenInbox((open) => !open)}
          className="flex w-full items-center border-b bg-green-50 px-4 py-3 text-left font-semibold text-green-800 hover:bg-green-100"
        >
          <EnvelopeIcon className="w-5" />
          <span className="pl-2">Kotak masuk</span>
          <span className="ml-auto rounded-full bg-green-700 px-2 text-xs text-white">
            {intrayEmailList.length}
          </span>
        </button>
        {openInbox &&
          intrayEmailList.map((email) => (
            <button
              type="button"
              key={email.id}
              className={
                "block w-full border-b px-4 py-3 text-left hover:bg-slate-100 " +
                (email.id === selectedEmail?.id && !composer
                  ? "border-l-2 border-l-cyan-600 bg-cyan-50"
                  : "")
              }
              onClick={() => {
                closeComposer();
                setSelectedEmail(email);
                setSelectedSentEmail(undefined);
                setOpenFile(false);
              }}
            >
              <div className="flex items-center gap-1 truncate text-sm font-semibold text-gray-800">
                {replyCount(email) > 0 && (
                  <CheckCircleIcon className="w-4 shrink-0 text-green-600" />
                )}
                {email.from_name}
              </div>
              <div className="mt-1 flex truncate text-sm text-gray-600">
                <ChevronRightIcon className="w-4 shrink-0" />
                {email.subject}
              </div>
            </button>
          ))}
        <button
          type="button"
          onClick={() => setOpenSent((open) => !open)}
          className="flex w-full items-center border-b bg-orange-50 px-4 py-3 text-left font-semibold text-orange-800 hover:bg-orange-100"
        >
          <PaperAirplaneIcon className="w-5" />
          <span className="pl-2">Terkirim</span>
          <span className="ml-auto rounded-full bg-orange-700 px-2 text-xs text-white">
            {sentEmailList.length}
          </span>
        </button>
        {openSent &&
          sentEmailList.map((email, index) => (
            <button
              type="button"
              key={email.id ?? index}
              onClick={() => {
                closeComposer();
                setSelectedEmail(undefined);
                setSelectedSentEmail(email);
                setOpenFile(false);
              }}
              className="block w-full border-b px-4 py-3 text-left hover:bg-slate-100"
            >
              <div className="truncate text-sm font-medium text-gray-700">
                {email.subject}
              </div>
              <div className="mt-1 truncate text-xs text-gray-500">
                Kepada: {email.sendto}
              </div>
            </button>
          ))}
      </aside>

      <main className="w-3/4 overflow-auto lg:w-4/5">
        {composer && (
          <Composer
            mode={composer.mode}
            values={values}
            emailAddress={emailAddress}
            draftStatus={draftStatus}
            saving={saving}
            sending={sending}
            conflict={conflict}
            onChange={updateValues}
            onSend={sendEmail}
            onDiscard={discardDraft}
            onReload={reloadDraft}
          />
        )}
        {!composer && selectedEmail && (
          <div className="h-full">
            <div className="sticky top-0 z-10 flex items-center border-b bg-white px-5 py-3">
              <h2 className="font-semibold text-gray-900">
                {selectedEmail.subject}
              </h2>
              <div className="ml-auto flex gap-3">
                <button
                  type="button"
                  onClick={() =>
                    openComposer({ mode: "reply", email: selectedEmail })
                  }
                  className="font-semibold text-cyan-700 hover:text-cyan-900"
                >
                  Balas
                </button>
                <button
                  type="button"
                  onClick={() =>
                    openComposer({ mode: "forward", email: selectedEmail })
                  }
                  className="font-semibold text-cyan-700 hover:text-cyan-900"
                >
                  Teruskan
                </button>
              </div>
            </div>
            {sentEmailList
              .filter(
                (item) =>
                  item.intray_simulation_email_id ===
                  selectedEmail.intray_simulation_email_id
              )
              .map((reply, index) => (
                <EmailCard key={reply.id ?? index} email={reply} sent />
              ))}
            <div className="border-b p-5">
              <p className="font-medium">{selectedEmail.from_name}</p>
              <p className="text-sm text-gray-500">
                Tanggal: {selectedEmail.date}
              </p>
              <div
                className="py-4 text-sm"
                dangerouslySetInnerHTML={{ __html: selectedEmail.content }}
              />
            </div>
          </div>
        )}
        {!composer && selectedSentEmail && (
          <EmailCard email={selectedSentEmail} sent />
        )}
        {!composer &&
          !selectedEmail &&
          !selectedSentEmail &&
          (openFile ? (
            <PdfViewer pdfUrl={props.activeSession.file_path ?? ""} />
          ) : (
            <div className="flex h-full items-center justify-center text-gray-400">
              <div className="flex h-40 w-40 flex-col items-center justify-center rounded-full border border-cyan-100 bg-cyan-50">
                <EnvelopeIcon className="w-10 text-cyan-300" />
                <span className="mt-2 font-semibold">Pilih email</span>
              </div>
            </div>
          ))}
      </main>
    </div>
  );
}

function Composer(props: {
  mode: TPIntrayDraftMode;
  values: ComposerValues;
  emailAddress: TReactSelect[];
  draftStatus: string;
  saving: boolean;
  sending: boolean;
  conflict: boolean;
  onChange: (next: Partial<ComposerValues>) => void;
  onSend: () => void;
  onDiscard: () => void;
  onReload: () => void;
}) {
  const canSend =
    props.values.sendTo.length > 0 &&
    props.values.subject.trim() !== "" &&
    !props.saving &&
    !props.sending &&
    !props.conflict;
  const title =
    props.mode === "compose"
      ? "Tulis email"
      : props.mode === "reply"
      ? "Balas email"
      : "Teruskan email";
  return (
    <div className="p-5">
      <div className="mb-4 flex items-center border-b border-gray-200 pb-4">
        <div>
          <h2 className="font-semibold text-gray-900">{title}</h2>
          <p
            className={
              "mt-1 text-sm " +
              (props.conflict ? "text-red-600" : "text-gray-500")
            }
          >
            {props.draftStatus}
          </p>
        </div>
        {props.conflict && (
          <button
            type="button"
            onClick={props.onReload}
            className="ml-auto inline-flex items-center rounded-md border border-cyan-600 px-3 py-2 text-sm font-semibold text-cyan-700 hover:bg-cyan-50"
          >
            <ArrowPathIcon className="mr-1.5 w-4" />
            Muat ulang draf
          </button>
        )}
      </div>
      <div className="space-y-3">
        <label className="flex items-center gap-3">
          <span className="w-16 text-sm font-medium text-gray-700">Kepada</span>
          <Select
            isMulti
            className="w-full"
            placeholder=""
            options={props.emailAddress}
            value={props.values.sendTo.map((value) => ({
              label: value,
              value,
            }))}
            onChange={(items) =>
              props.onChange({ sendTo: items.map((item) => item.value) })
            }
          />
        </label>
        <label className="flex items-center gap-3">
          <span className="w-16 text-sm font-medium text-gray-700">Cc</span>
          <Select
            isMulti
            className="w-full"
            placeholder=""
            options={props.emailAddress}
            value={props.values.copyTo.map((value) => ({
              label: value,
              value,
            }))}
            onChange={(items) =>
              props.onChange({ copyTo: items.map((item) => item.value) })
            }
          />
        </label>
        <label className="flex items-center gap-3">
          <span className="w-16 text-sm font-medium text-gray-700">Subjek</span>
          <input
            type="text"
            value={props.values.subject}
            onChange={(event) =>
              props.onChange({ subject: event.currentTarget.value })
            }
            className="w-full rounded border border-gray-300 px-3 py-2 focus:border-cyan-500 focus:outline-none"
          />
        </label>
        <InTrayPasteGuard>
          <ReactQuill
            modules={{
              toolbar: [
                [{ header: [1, 2, false] }],
                [
                  "bold",
                  "italic",
                  "underline",
                  { color: [] },
                  "strike",
                  "blockquote",
                ],
                [
                  { list: "ordered" },
                  { list: "bullet" },
                  { indent: "-1" },
                  { indent: "+1" },
                  { align: [] },
                ],
              ],
            }}
            theme="snow"
            value={props.values.content}
            onChange={(content) => props.onChange({ content })}
          />
        </InTrayPasteGuard>
        <div className="flex gap-2 pt-2">
          <button
            type="button"
            disabled={!canSend}
            onClick={props.onSend}
            className="rounded-md bg-cyan-600 px-4 py-2 text-sm font-semibold text-white hover:bg-cyan-700 disabled:cursor-not-allowed disabled:bg-gray-300"
          >
            {props.sending ? "Mengirim..." : "Kirim"}
          </button>
          <button
            type="button"
            disabled={props.sending}
            onClick={props.onDiscard}
            className="rounded-md border border-cyan-600 px-4 py-2 text-sm font-semibold text-cyan-700 hover:bg-cyan-50 disabled:border-gray-300 disabled:text-gray-400"
          >
            Hapus draf
          </button>
        </div>
      </div>
    </div>
  );
}

function EmailCard(props: {
  email: TPQuestionaryReplyEmail | TPQuestionaryNewEmail;
  sent: boolean;
}) {
  return (
    <div className="border-b p-5">
      <div className="flex items-center gap-2 font-medium text-gray-900">
        {props.sent && <CheckCircleIcon className="w-5 text-green-600" />}
        {props.email.subject}
      </div>
      <p className="mt-1 text-sm text-gray-500">
        Kepada: {props.email.sendto}
      </p>
      <p className="text-sm text-gray-500">Cc: {props.email.copyto || "-"}</p>
      <div
        className="py-4 text-sm"
        dangerouslySetInnerHTML={{ __html: props.email.content }}
      />
    </div>
  );
}

export default IntrayViewer;
